/**
 * Data portability + account deletion controls.
 *
 * Lives under the "General" Settings tab and is SUPER_ADMIN-only. Two
 * operations:
 *
 *   1. **Export all organization data** — downloads a ZIP containing one
 *      JSON per module plus a ``documents/`` folder with the raw uploaded
 *      files. Required under LGPD / GDPR / CCPA for data portability.
 *
 *   2. **Request data deletion** — schedules the org for permanent
 *      deletion after a 30-day grace period. The SUPER_ADMIN must re-type
 *      the org's exact name to confirm (GitHub-repo style). The org stays
 *      usable during the grace window; a cross-app banner reminds all
 *      users of the scheduled date.
 */
import { useEffect, useState } from "react";
import { Download, AlertTriangle, ShieldAlert } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";
import { downloadOrgExportZip } from "@/api/exports";
import {
  getCurrentOrg,
  getOrgDeletionStatus,
  requestOrgDeletion,
} from "@/api/organizations";
import type { DeletionStatus, Organization } from "@/types/organization";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function DataAndDeletionSettings() {
  const { addToast } = useToast();

  const [org, setOrg] = useState<Organization | null>(null);
  const [deletion, setDeletion] = useState<DeletionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const [exporting, setExporting] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationName, setConfirmationName] = useState("");
  const [submittingDeletion, setSubmittingDeletion] = useState(false);

  useEffect(() => {
    Promise.all([getCurrentOrg(), getOrgDeletionStatus()])
      .then(([o, d]) => {
        setOrg(o);
        setDeletion(d);
      })
      .catch(() => addToast("error", "Failed to load organization status"))
      .finally(() => setLoading(false));
  }, [addToast]);

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadOrgExportZip();
      addToast(
        "success",
        "Export complete — check your Downloads folder.",
      );
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Could not generate the export.",
      );
    } finally {
      setExporting(false);
    }
  };

  const openConfirm = () => {
    setConfirmationName("");
    setConfirmOpen(true);
  };

  const onConfirmDeletion = async () => {
    if (!org) return;
    if (confirmationName !== org.name) {
      addToast(
        "error",
        "The confirmation text must match the organization name exactly.",
      );
      return;
    }
    setSubmittingDeletion(true);
    try {
      const status = await requestOrgDeletion(confirmationName);
      setDeletion(status);
      setConfirmOpen(false);
      addToast(
        "success",
        "Deletion scheduled. You have 30 days to change your mind — contact support@lenavio.com to cancel.",
      );
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Could not schedule deletion.",
      );
    } finally {
      setSubmittingDeletion(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-text-muted">Loading…</p>;
  }

  const isPending = deletion?.status === "PENDING_DELETION";

  return (
    <div className="space-y-6">
      {/* ─── Export ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export all organization data
          </CardTitle>
        </CardHeader>
        <p className="text-sm text-text-muted mb-4">
          Download a ZIP archive containing every record tied to your
          organization — employees, leave requests, time entries, complaints
          (with internal HR notes redacted), documents with their original
          files, invoices, and the full audit trail. Required under LGPD,
          GDPR, and CCPA for data portability. Large organizations may take
          a moment to package.
        </p>
        <div className="flex justify-end">
          <Button onClick={onExport} isLoading={exporting}>
            <Download className="h-4 w-4 mr-1" />
            {exporting ? "Preparing archive…" : "Export all data (.zip)"}
          </Button>
        </div>
      </Card>

      {/* ─── Deletion ───────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-danger">
            <ShieldAlert className="h-5 w-5" />
            Request data deletion
          </CardTitle>
        </CardHeader>

        {isPending ? (
          <div className="rounded-lg border border-danger/30 bg-danger/10 p-4 space-y-2">
            <p className="text-sm font-medium text-text-primary">
              This organization is scheduled for deletion on{" "}
              {formatDate(deletion?.deletion_scheduled_for ?? null)}.
            </p>
            <p className="text-xs text-text-muted">
              Requested on{" "}
              {formatDate(deletion?.deletion_requested_at ?? null)}. To cancel
              the deletion, email{" "}
              <a
                href="mailto:support@lenavio.com"
                className="text-accent hover:underline"
              >
                support@lenavio.com
              </a>{" "}
              before the scheduled date.
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-warning/30 bg-warning/10 p-4 mb-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
              <div className="text-sm text-text-primary">
                <p className="font-medium">This action cannot be undone.</p>
                <p className="text-xs text-text-muted mt-1">
                  Requesting deletion will permanently erase all organization
                  data — employee records, documents, complaints, invoices,
                  and audit history — after a 30-day grace period. Your
                  account will be deactivated immediately at the end of that
                  window. You can cancel during the grace period by emailing
                  support@lenavio.com.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="danger" onClick={openConfirm}>
                Request data deletion
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* ─── Confirm-by-typing-name modal ───────────────────────── */}
      <Modal
        isOpen={confirmOpen}
        onClose={() => !submittingDeletion && setConfirmOpen(false)}
        title="Confirm permanent deletion"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            You're about to schedule <strong>{org?.name}</strong> for
            permanent deletion in 30 days. All employees, documents,
            complaints, invoices, and audit records will be erased. This
            action cannot be undone after the grace period.
          </p>
          <div>
            <p className="text-xs text-text-muted mb-1">
              To confirm, type the organization name{" "}
              <code className="text-text-primary font-medium">
                {org?.name}
              </code>{" "}
              below:
            </p>
            <Input
              value={confirmationName}
              onChange={(e) => setConfirmationName(e.target.value)}
              placeholder={org?.name ?? ""}
              autoFocus
              autoComplete="off"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={submittingDeletion}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={onConfirmDeletion}
              isLoading={submittingDeletion}
              disabled={!org || confirmationName !== org.name}
            >
              Schedule deletion
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
